import { applyToPoint, lengthNearest, ROOT_ID, type Rect } from '@flobi/artwork-core';
import { hexColor } from '../colors';
import type { IconName } from '../chrome/icons';
import type { Point, RailButton, Tool, ToolContext } from './types';

/** How big new text starts, in document units. */
const TEXT_SIZE = 32;
/**
 * How tall new text has to come out on screen, in pixels.
 *
 * A document unit is a point, so 32pt text on a poster seen whole is six screen pixels —
 * and the caret is drawn 1.5 SCREEN pixels wide whatever the zoom. That pairing is what
 * makes a working text box look like a broken one: a bright bar blinking over a smudge,
 * with no way to tell typing from a dead keyboard. Text you are being asked to type into
 * has to be text you can read.
 */
const LEGIBLE = 11;
/** The size slider's range, which a size chosen for legibility has to stay inside. */
const SIZES = { min: 8, max: 240 };
/** How far the pointer has to travel before a press is drawing a box rather than clicking. */
const DRAG_SLOP = 3;
/**
 * What a new text box says before anyone types.
 *
 * It arrives selected, so the first keystroke replaces the whole of it and nobody has to
 * clear it — which is also why a placeholder is better than an empty box: an empty box is
 * invisible until you have typed into it, and this one shows the size, the colour and the
 * place you are about to type in before you commit a single character.
 */
const PLACEHOLDER = 'Lorem beirut';
/**
 * The colour new text is set in.
 *
 * Not the rail's fill: that one colour is shared by every tool, so text took whatever the
 * last shape was painted with, which is how it kept arriving in the last shape's colour.
 * It is its own constant instead, and black because that is what ink is — the selection
 * wash inverts what it covers, so selected characters come out white without the text
 * having to be white to begin with.
 */
const TEXT_COLOUR = '#000000';

/**
 * What a press of the type tool means.
 *
 * Illustrator keeps these behind a flyout on the T, the same way it keeps the pen's four;
 * they are the rail's top well instead, which is where a tool says how it works. The
 * difference between them is only what the text is laid out INTO — a line that grows, or
 * a box that wraps — so they are one tool with modes rather than two tools.
 */
type TypeMode = 'point' | 'area' | 'path';

const MODES: { id: TypeMode; icon: IconName; title: string }[] = [
  { id: 'point', icon: 'text', title: 'Point type — type where you click' },
  { id: 'area', icon: 'text-area', title: 'Area type — drag a box and type inside it' },
  { id: 'path', icon: 'text-path', title: 'Type on a path — press a path and the words ride it' },
];

/**
 * The text tool: click where the first line should start and type, or drag the box to
 * type into.
 *
 * Typing itself belongs to the editor rather than the tool — a text layer can be opened
 * from the select tool too — so this creates the layer and hands over.
 */
export function textTool(family: string): Tool {
  /**
   * The size the rail shows — the one the last text came out in — and the size the person
   * chose on it. Kept apart so a raise for legibility while zoomed out does not stick: it
   * used to be written over the choice, and every text after it came out huge even at
   * 100% (QA 2026-09-24, text-F20).
   */
  const settings = { size: TEXT_SIZE, chosen: TEXT_SIZE };
  /** Which of the two a press means. Kept across tool changes, as a mode should be. */
  let mode: TypeMode = 'point';
  /** The box being dragged out, in area mode. */
  let drawing: { id: string; from: Point; origin: Point; grew: boolean; line: number } | null = null;
  /** A press landed in the text being typed, so the drag after it sweeps a selection. */
  let typing = false;
  /**
   * The text the last press made, until the next press.
   *
   * A double click on empty canvas is two presses: the first makes a box with its
   * placeholder selected, and the second lands inside that very box — as a double click,
   * which takes the word under it. So you got 'Lorem' selected and typed 'Hello beirut'
   * (QA 2026-09-24, text-F5). A second press on a box the first one just made keeps the
   * whole placeholder chosen instead.
   */
  let justMade: string | null = null;

  /**
   * The size the next box is set in, raised if that would be too small to read here.
   *
   * The new size goes back to the rail rather than being used behind it: a slider reading
   * 32 over 61pt text is exactly the kind of lie the swatches were just cured of, and the
   * number you see has to be the number you got. The raise is worked out from the size
   * CHOSEN each time, so zoomed back in, the chosen size comes back (text-F20).
   */
  const legibleSize = (ctx: ToolContext): number => {
    const wanted = Math.round(Math.min(SIZES.max, Math.max(settings.chosen, LEGIBLE * ctx.unitsPerPixel)));
    if (wanted !== settings.size) {
      settings.size = wanted;
      ctx.railChanged();
    }
    return settings.size;
  };

  /**
   * The path under the pointer, if there is one within reach of its outline.
   *
   * On the OUTLINE rather than merely inside a filled shape: type rides the line, so
   * being in the middle of a closed curve is not being anywhere the type could go.
   */
  const pathUnder = (ctx: ToolContext, world: Point): string | null => {
    const reach = 10 * ctx.unitsPerPixel;
    const around: Rect = { x: world.x - reach, y: world.y - reach, width: 2 * reach, height: 2 * reach };
    for (const id of ctx.nearby(around)) {
      const node = ctx.node(id);
      if (node?.type !== 'path' || node.locked) continue;
      if (ctx.points.segmentNear(world, id)) return id;
    }
    return null;
  };

  /** A new text node, at a point, in whichever way the mode lays text out. */
  const create = (ctx: ToolContext, world: Point, area: boolean, onPath?: { id: string; start: number }) => {
    const style = ctx.style;
    const size = legibleSize(ctx);
    const parent = ctx.artboardAt(world);
    const parentOrigin = parent ? ctx.scene.worldTransform(parent) : [1, 0, 0, 1, 0, 0];
    // Point type sits ON the line you clicked, so the click is the baseline and the box
    // hangs above it. A box you dragged is exactly the box you dragged.
    const lift = area ? 0 : size * 0.4;
    const origin = { x: world.x - parentOrigin[4]!, y: world.y - parentOrigin[5]! - lift };
    const result = ctx.edit([
      {
        op: 'create',
        ref: 'text',
        parentId: parent ?? ROOT_ID,
        node: {
          type: 'text',
          name: 'Text',
          // The box you dragged is the box you get: it wraps at that width and keeps that
          // height. Auto-height would have grown past the height you drew, which makes the
          // drag's second dimension a thing you set and then watched be ignored.
          ...(onPath ? { onPath } : {}),
          sizing: area ? 'fixed' : 'auto-width',
          width: area ? 1 : 0,
          height: area ? 1 : 0,
          opacity: style.opacity,
          transform: [1, 0, 0, 1, origin.x, origin.y],
          runs: [{ text: PLACEHOLDER }],
          style: {
            fontFamily: family,
            fontSize: size,
            fills: [{ type: 'solid', color: hexColor(TEXT_COLOUR) }],
          },
        },
      },
    ]);
    return result.ok ? { id: result.created.text!, origin } : null;
  };

  /**
   * The text already on the page under a press, if there is one the tool may open.
   *
   * The type tool used to test only the text being typed; a press on any OTHER text
   * dropped a new placeholder on top of it, so there was no way to click into a heading
   * with the tool in hand (QA 2026-09-24, text-F5). `hit` already passes over locked and
   * hidden layers, which is what makes a text one the tool may open.
   *
   * Type on a curve counts only when the press is on its own curve: a press on some
   * other path with the path mode chosen is aimed at that path.
   */
  const textUnder = (ctx: ToolContext, world: Point, path: string | null): string | null => {
    const hit = ctx.hit(world);
    if (!hit) return null;
    let found: string | null = hit;
    // A text inside a group is hit as its group. The text the press is on is the last one
    // in paint order whose box holds it, which is the one drawn on top.
    if (ctx.node(hit)?.type === 'group') {
      found = null;
      for (const id of ctx.scene.subtree(hit)) {
        const inner = ctx.node(id);
        if (inner?.type !== 'text' || !ctx.scene.choosable(id)) continue;
        const box = ctx.boundsOf(id);
        if (box && world.x >= box.x && world.y >= box.y && world.x <= box.x + box.width && world.y <= box.y + box.height) found = id;
      }
    }
    const node = found ? ctx.node(found) : undefined;
    if (!found || node?.type !== 'text') return null;
    if (path && node.onPath?.id !== path) return null;
    return found;
  };

  /**
   * Typing moves to a text, and then the selection follows it.
   *
   * In that order: choosing another layer while a text is open ends the typing, and
   * ending it hands the canvas back to Select — so selecting first left a new or opened
   * text chosen, with the tool gone and no caret in it. Moved to first, the text being
   * typed in is already the chosen one when the selection changes.
   */
  const begin = (ctx: ToolContext, id: string, at: number | 'all' | Point) => {
    if (ctx.text?.current && ctx.text.editingId !== id) ctx.stopCapturing();
    if (typeof at === 'object') ctx.text?.startAt(id, at);
    else ctx.text?.start(id, at);
    ctx.setSelection([id]);
  };

  /** Opens a text already on the page at the press, and lets the drag after it sweep. */
  const open = (ctx: ToolContext, id: string, world: Point): true => {
    begin(ctx, id, world);
    typing = true;
    return true;
  };

  return {
    id: 'text',

    onPointerDown(event, world, ctx) {
      const previous = justMade;
      justMade = null;
      // A press inside the box being typed into belongs to the caret. Without this the
      // type tool answers a click in its own text by starting ANOTHER box on top of it,
      // and there is no way to put the caret somewhere with the tool still in hand.
      // How many presses it is decides how much it takes — one the caret, two the word,
      // three the lot. The canvas counts the presses, because a PointerEvent carries no
      // click count of its own.
      if (ctx.text?.current && ctx.text.hits(world)) {
        // The second press of a double click that made this very box: the placeholder
        // stays chosen whole, so typing replaces all of it (text-F5).
        if (ctx.clicks >= 2 && previous === ctx.text.editingId) ctx.text.selectAll();
        else if (ctx.clicks >= 3) ctx.text.selectAll();
        else if (ctx.clicks === 2) ctx.text.selectWordAt(world);
        else {
        // Only a single click starts a sweep. A double click sends a move of its own
        // between its two presses, and letting that extend from the word's start
        // collapsed the word straight back to a caret — you saw the selection flash
        // and vanish.
          ctx.text.pointTo(world, event.shiftKey);
          typing = true;
        }
        return true;
      }

      // A press on a text already on the page opens it there, whatever the mode (text-F5).
      const path = mode === 'path' ? pathUnder(ctx, world) : null;
      const existing = textUnder(ctx, world, path);
      if (existing) return open(ctx, existing, world);

      if (mode === 'path') {
        // Press the curve, and the type lives on it from that point onward. The press is
        // where the first character goes, so you aim at the place the words should start
        // rather than at the path as a whole.
        const id = path;
        if (!id) return true;
        const ruler = ctx.rulerOf(id);
        const start = ruler ? Math.max(0, lengthNearest(ruler, world)) : 0;
        const made = create(ctx, world, false, { id, start });
        if (!made) return false;
        // The curve stops being drawn. A path carrying type is scaffolding — it is there
        // to say where the words go, not to be seen alongside them — so it keeps its
        // points and its paint goes dark, and the selection overlay is how you find it
        // again. Switched off rather than thrown away: deleting the text used to leave a
        // path with no fill or stroke at all, invisible for good (QA 2026-09-24, text-F10),
        // where now its Style tab still holds the paint, one switch from showing again.
        const guide = ctx.node(id) as { fills?: { visible?: boolean }[]; strokes?: { visible?: boolean }[] } | undefined;
        const dark = (paints: { visible?: boolean }[] | undefined) => (paints ?? []).map((paint) => ({ ...paint, visible: false }));
        ctx.edit([{ op: 'set', id, props: { fills: dark(guide?.fills), strokes: dark(guide?.strokes) } }]);
        begin(ctx, made.id, 'all');
        justMade = made.id;
        return true;
      }

      if (mode === 'point') {
        const made = create(ctx, world, false);
        if (!made) return false;
        begin(ctx, made.id, 'all');
        justMade = made.id;
        return true;
      }

      // The box is written as it is dragged rather than on release, so other people watch
      // it being drawn — the same reason the shapes and the pen write as they go.
      //
      // Snapped from the corner it starts at, like any other box — and only here, at the
      // end: a press that lands on type already being edited, or on a path to run words
      // along, is aiming at the ARTWORK and has to be read where the hand actually was.
      const corner = ctx.place(world);
      const box = create(ctx, corner, true);
      if (!box) return false;
      drawing = { id: box.id, from: corner, origin: box.origin, grew: false, line: Math.ceil(settings.size * 1.25) };
      // Typing elsewhere moves to the new box now rather than on release: choosing the box
      // while another text is open would end that typing, and ending it hands the canvas
      // back to Select in the middle of the drag.
      if (ctx.text?.current) begin(ctx, box.id, 'all');
      else ctx.setSelection([box.id]);
      justMade = box.id;
      return true;
    },

    onPointerMove(event, world, ctx) {
      if (mode === 'path' && event.buttons === 0) {
        ctx.setCursor(pathUnder(ctx, world) ? 'copy' : null);
        return;
      }
      if (typing) {
        if (event.buttons !== 0) ctx.text?.pointTo(world, true);
        return;
      }
      if (!drawing || event.buttons === 0) return;
      const reach = DRAG_SLOP * ctx.unitsPerPixel;
      if (!drawing.grew && Math.abs(world.x - drawing.from.x) < reach && Math.abs(world.y - drawing.from.y) < reach) return;
      drawing.grew = true;

      // The corner the drag started at stays put and the box grows towards the pointer,
      // so it can be drawn in any direction. The slop above is measured from the raw
      // pointer, because whether the hand has MOVED is a fact about the hand.
      const at = ctx.place(world, [drawing.id]);
      const width = Math.max(1, Math.abs(at.x - drawing.from.x));
      // At least one line tall. A fixed box now clips its text, so a drag that barely
      // moved up or down made a box too flat to show the line typed into it
      // (QA 2026-09-24, text-F17). Grown downward from the corner either way.
      const height = Math.max(drawing.line, Math.abs(at.y - drawing.from.y));
      const x = Math.min(at.x, drawing.from.x) - (drawing.from.x - drawing.origin.x);
      const y = (at.y < drawing.from.y && drawing.from.y - at.y >= drawing.line ? at.y : drawing.from.y) - (drawing.from.y - drawing.origin.y);
      ctx.edit([{ op: 'set', id: drawing.id, props: { width, height, transform: [1, 0, 0, 1, x, y] } }]);
    },

    onPointerUp(_event, _world, ctx) {
      if (typing) {
        typing = false;
        ctx.stopCapturing();
        return;
      }
      const box = drawing;
      if (!box) return;
      drawing = null;
      // A click in area mode is a click, not a box a pixel wide: it gives point type,
      // which is what someone who clicked plainly meant.
      if (!box.grew) ctx.edit([{ op: 'set', id: box.id, props: { sizing: 'auto-width', width: 0, height: 0 } }]);
      ctx.stopCapturing();
      ctx.text?.start(box.id, 'all');
    },

    deactivate() {
      drawing = null;
      typing = false;
    },

    /**
     * The box, while it is being dragged and while it is being typed into.
     *
     * An empty text node paints nothing, so without this a box is drawn entirely blind:
     * you press, move, release, and the only thing that ever appears is a caret. It stays
     * up while the box is being typed into as well, because a box you cannot see is a
     * wrap width you cannot judge — and an empty one is otherwise invisible.
     */
    draw(overlay, ctx) {
      const id = drawing?.id ?? ctx.text?.editingId;
      if (!id) return;
      const node = ctx.node(id);
      if (node?.type !== 'text' || node.sizing === 'auto-width') return;
      // Its four corners through the text's whole transform. Sized from the scale terms
      // alone, a turned box shrank, and at 90° it vanished (QA 2026-09-24, text-F26).
      const at = ctx.scene.worldTransform(id);
      const corners = [
        applyToPoint(at, 0, 0),
        applyToPoint(at, node.width, 0),
        applyToPoint(at, node.width, node.height),
        applyToPoint(at, 0, node.height),
      ];
      const look = { width: 1, alpha: drawing ? 1 : 0.5 };
      corners.forEach((corner, index) => overlay.line(corner, corners[(index + 1) % 4]!, look));
    },

    /**
     * The two ways text is laid out above, and how big it is set below.
     *
     * No style block and no opacity well. Text starts white and carries no outline, so a
     * fill swatch here would set nothing and an outline size would set nothing twice —
     * and a control that does nothing is worse on the rail than missing from it, since
     * the rail is the one surface that is supposed to answer to the tool in hand. The
     * colour of text already on the page is the inspector's, with everything else about
     * something already drawn.
     */
    rail(ctx: ToolContext) {
      const items: RailButton[] = MODES.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        pressed: mode === item.id,
        run: () => {
          mode = item.id;
          ctx.railChanged();
        },
      }));
      return {
        top: { kind: 'buttons' as const, items },
        bottom: {
          kind: 'slider' as const,
          icon: 'text' as const,
          title: 'Size',
          value: settings.size,
          min: SIZES.min,
          max: SIZES.max,
          format: (value: number) => `${value}`,
          onChange: (size: number) => {
            settings.size = size;
            settings.chosen = size;
            ctx.railChanged();
          },
        },
      };
    },
  };
}
